import React from 'react';
import type { TipsMapFunType, TipsMapType } from '../index';
import {
  brandHealthCh,
  brandHealthEn,
  sentimentCh,
  sentimentEn,
  validationCh,
  validationEn,
} from './utils';

export const commonTimeTipsText: TipsMapFunType = (
  time,
  quickOptions,
): TipsMapType => ({
  en: (
    <>
      Two time options are available:
      <br />
      Start & End time: Use the start to end time of the time filter. For
      example, after saving the scheme, the time filter will fix the query time
      [{time.join(' to ')}].
      <br />
      Date Duration: The shortcut time option selected for the last time is
      displayed here. For example, after saving the scheme, the time filter will
      fix the query time [{quickOptions}].
    </>
  ),
  zh: (
    <>
      提供两种时间选择方式：
      <br />
      开始&结束时间：使用时间筛选器的开始至结束时间。举例，本方案保存后，该方案将固定查询【
      {time.join('~')}】的数据。
      <br />
      固定时间间隔：时间筛选器最近1次选中的右侧快捷时间选项，将显示在此处。举例，本方案保存后，将使用【
      {quickOptions}
      】作为固定时间选项进行查询。
    </>
  ),
});

// export const commonSentimentTips: TipsMapType = {
//   zh: (
//     <>
//       整句情感分：情感分被用来衡量所有评论的情感倾向，分值范围为1-5。情感分大于3.5为正面，小于2.5为负面，在2.5到3.5之间为中性。
//       <br />
//       其经由每条被纳入计算的评论的情感分判定结果进行统计，每条评论的情感分将会有3种取值，负面（1分），中性（3分）和正面（5分）。您可以在评论/资讯详情页面针对单条评论修正其情感分的判断结果。
//       <br />
//       <br />
//       {sentimentCh}
//     </>
//   ),
//   en: (
//     <>
//       Total Sentiment: Sentiment score is used to score the sentiment tendency
//       of all reviews, on a scale of 1-5. Sentiment score greater than 3.5 is
//       positive, score lower than 2.5 is negative, and score between 2.5-3.5 is
//       neutral.
//       <br />
//       It is calculated based on the sentiment score of each included review.
//       There will be three possible values for the sentiment score of each
//       review: negative (1), neutral (3), and positive (5). You can always modify
//       the sentiment score for a single review on the Feeds page.
//       <br />
//       <br />
//       {sentimentEn}
//     </>
//   ),
// };

export const commonSentimentTips: TipsMapType = {
  zh: (
    <>
      情感分：DataBrain基于多语种NLP情感分模型，给每1条评论赋予情感分值，分为：负面(1分),中性(3分),正面(5分)。汇总情感分为各条评论情感分加总求平均，分值范围为1-5。
      <br />
      <br />
      {sentimentCh}
    </>
  ),
  en: (
    <>
      Sentiment: The DataBrain Multilingual Sentiment Analysis model assigns a
      sentiment score to each review, rating them as Negative (1 point), Neutral
      (3 points), or Positive (5 points). The aggregate sentiment score is
      calculated by averaging the sum of individual review scores, with a range
      from 1 to 5.
      <br />
      <br />
      {sentimentEn}
    </>
  ),
};

export const commonTopicSentimentTips: TipsMapType = {
  zh: (
    <>
      该筛选器仅对单一话题生效，选择后默认按照话题筛选器所选话题的情感分筛选评论。若选择多个话题，则该筛选器不生效。
    </>
  ),
  en: (
    <>
      This filter only applies to a single topic. Once selected, it will
      automatically sort the reviews based on the sentiment score of the chosen
      topic. If multiple topics are selected, this filter will not take effect.
    </>
  ),
};

export const commonBrandHealthTips: TipsMapType = {
  zh: (
    <>
      品牌健康度：基于情感分对不同渠道和不同有效性评论进行加权计算，去除过多中性评论和无效评论的影响，变化趋势对于游戏正负事件更敏感。分数从-100到100，越高表示舆情反馈越积极。只有当正面声量和负面声量的总和超过10才计算品牌健康度。
      <br />
      {brandHealthCh}
    </>
  ),
  en: (
    <>
      Brand Health: The Brand Health Score is calculated by weighting sentiment across different channels and varying levels of reviews validation. After filtering out excessive neutral and invalid reviews, the trend is more sensitive to positive and negative events. The score ranges from -100 to 100, with higher scores indicating more positive feedback. Available for calculation only if the combined total of positive and negative mentions is greater than 10.
      <br />
      {brandHealthEn}
    </>
  ),
};

export const commonSentimentAndNetSentimentTips: TipsMapType = {
  zh: (
    <>
      情感分：情感分被用来衡量所有评论的情感倾向，情感分被用来衡量剔除无效和低有效性评论后的评论的情感倾向，分值范围均为1-5。分值大于3.5为正面，小于2.5为负面，在2.5到3.5之间为中性。
      <br />
      其均经由每条被纳入计算的评论的情感分判定结果进行统计，每条评论的情感分将会有3种取值，负面（1分），中性（3分）和正面（5分）。您可以在评论/资讯详情页面针对单条评论其情感分的判断结果。
      <br />
      <br />
      {sentimentCh}
    </>
  ),
  en: (
    <>
      Sentiment: Sentiment score is used to score the sentiment tendency of all
      reviews, while sentiment score is used to score the sentiment tendency of
      reviews that excluded those with low validation and even invalid ones.
      They are both on a scale of 1-5. The value of the score greater than 3.5
      is positive, lower than 2.5 is negative, and between 2.5-3.5 is neutral.
      <br />
      They are both calculated based on the sentiment score of each included
      review. There will be three possible values for the sentiment score of
      each review: negative (1), neutral (3), and positive (5). You can always
      modify the sentiment score for a single review on the Feeds page.
      <br />
      <br />
      {sentimentEn}
    </>
  ),
};

export const commonValidationTips: TipsMapType = {
  zh: (
    <>
      有效性：有效性指标用于根据文本的含义对游戏评论进行分类。无效评论指那些无法构成有意义句子的评论；低有效性评论指含义不明或与游戏无关的评论；中有效性评论是有意义且有游戏相关的句子，但不包含情感观点，只是事实陈述；高有效性评论指有意义且和游戏相关，并包含主观观点的评论。
      <br />
      <br />
      {validationCh}
    </>
  ),
  en: (
    <>
      Validation: The Validation metric is used to categorize game reviews based
      on the meaning of the text. Invalid reviews are not valid to be a
      meaningful sentence; low-validation reviews are not meaningful or not
      relevant to game; medium-validation reviews are meaningful and relevant to
      game but do not contain a subjective opinion, only statement;
      high-validation reviews are meaningful and relevant to game and contain a
      subjective opinion.
      <br />
      <br />
      {validationEn}
    </>
  ),
};

export const commonSentimentSentencesTips: TipsMapType = {
  zh: '情感分拆句只应用在英文评论，勾选将会同时展示英文翻译。',
  en: 'Sentiment sentences only apply to English comments. Checking the box will also display the English translation.',
};

export const commonGPTDisableTips: TipsMapType = {
  zh: '当前筛选条件下无评论，请调整筛选项。',
  en: 'No reviews under current filters. Please adjust your selections',
};

const brandHealthList = [
  {
    text: 'Very Unhealthy',
    textCh: '非常不健康',
    tips: (text = '健康度') => `${text} < -70`,
  },
  {
    text: 'Unhealthy',
    textCh: '不健康',
    tips: (text = '健康度') => `-70 ≤ ${text} < -25`,
  },
  {
    text: 'Mixed',
    text_value: 'Mixed-1',
    textCh: '中性',
    tips: (text = '健康度') => `-25 ≤ ${text} < 25`,
  },
  {
    text: 'Healthy',
    textCh: '健康',
    tips: (text = '健康度') => `25 ≤ ${text} ≤ 50`,
  },
  {
    text: 'Very Healthy',
    textCh: '非常健康',
    tips: (text = '健康度') => `${text} > 50`,
  },
];

export const commonBrandHealthGradeTips: (value: string) => TipsMapType = (
  value,
) => {
  function getCurrent(
    text: string,
    text_value?: string,
    language: 'zh' | 'en' = 'zh',
  ) {
    const showTextMap = {
      zh: '当前评分',
      en: 'Current',
    };
    const isCurrent = (text_value || text) === value;
    return {
      className: isCurrent ? 'current' : '',
      showText: isCurrent ? `(${showTextMap[language]})` : '',
    };
  }
  return {
    zh: (
      <div className="dbo-brand-health-grade">
        {brandHealthList.map(({ text, textCh, tips, text_value }) => {
          const { className, showText } = getCurrent(text, text_value);
          return (
            <div
              key={textCh}
              className={`dbo-brand-health-grade-item ${className}`}
            >
              <div className="dbo-brand-health-grade-item-title">
                {textCh}
                {showText}
              </div>
              <div className="dbo-brand-health-grade-item-desc">{tips()}</div>
            </div>
          );
        })}
      </div>
    ),
    en: (
      <div className="dbo-brand-health-grade">
        {brandHealthList.map(({ text_value, text, tips }) => {
          const { className, showText } = getCurrent(text, text_value, 'en');
          return (
            <div
              key={text}
              className={`dbo-brand-health-grade-item ${className}`}
            >
              <div className="dbo-brand-health-grade-item-title">
                {text}
                {showText}
              </div>
              <div className="dbo-brand-health-grade-item-desc">
                {tips('Score')}
              </div>
            </div>
          );
        })}
      </div>
    ),
  };
};

export const commonEventSettingTips: TipsMapType = {
  en: 'When too many events are selected, some may not be fully displayed on the chart due to space constraints. Please click on [Event Settings] to reduce the number of selected events. To view the total number of events, hover your mouse over the numbers below the axis.',
  zh: '所选事件过多时，可能导致图表上事件显示不全，请点击进入【事件设置】，减少当前所选事件；查看所有事件数量，请将鼠标悬浮在坐标轴下方的数字上',
};


export const commonCountryAndRegionTips: TipsMapType = {
  en: 'Unidentified Country',
  zh: '未识别国家',
};


export const commonClickDetailPageTips: TipsMapType = {
  en: 'Click to view related reviews',
  zh: '点击查看相关评论',
};
